// src/pages/WhyHowDetailPage.js

import { metLiveStijlen } from '../utils/doorloopjePdf';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';
import { useDoorloopjes } from '../context/DoorloopjesContext';
import { trackEvent } from '../utils/track';
import Beschrijving from '../components/Beschrijving';
// jsPDF and html2canvas are loaded dynamically in handleDownloadPDF() to reduce bundle size

import { ReactComponent as DownloadIcon } from '../assets/icon-download.svg';
import { ReactComponent as ChevronUpIcon } from '../assets/icon-chevron-up.svg';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

// Helper: proxify image URLs to avoid CORS issues
const proxify = (url) =>
  `${process.env.REACT_APP_STRAPI_URL}/api/media-proxy?url=${encodeURIComponent(url)}`;

const breadcrumbTranslations = {
  why: 'Waarom',
  how: 'Hoe'
};

function WhyHowDetailPage() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { globalSettings } = useDoorloopjes();

  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const pdfContentRef = useRef(null);

  useEffect(() => {
    const pathSegment = location.pathname.split('/')[1];
    const apiEndpoint = pathSegment === 'why' ? 'whies' : 'hows';
    
    const jwt = localStorage.getItem('jwt');
    const fetchContent = async () => {
      setLoading(true);
      setError('');
      try {
        const populateQuery = 'populate[steps][populate]=image';
        const url = `${STRAPI_URL}/api/${apiEndpoint}?filters[slug][$eq]=${slug}&${populateQuery}`;
        
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        
        if (response.data.data.length > 0) {
          setContent(response.data.data[0]);
        } else {
          setError('Deze pagina bestaat niet of is verplaatst.');
        }
      } catch (err) {
        setError('Deze pagina laden lukte niet. Probeer het opnieuw.');
        console.error("API Fout:", err.response?.data || err.message || err);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [slug, location.pathname]);

  const handleDownloadPDF = async () => {
    if (!content || !pdfContentRef.current) return;

    setIsGeneratingPDF(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      const element = pdfContentRef.current;

      // Dynamic import to reduce bundle size
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      // Always use multi-page approach with portrait orientation and vertical stacking
      {
        // Multi-page approach for all Why/How content
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = 210;
        const pageHeight = 297;
        const margin = 10;
        let currentY = margin;

        // Get all major sections that should not break
        const sections = element.querySelectorAll('.pdf-header, .pdf-description, .pdf-step, .pdf-footer');

        for (let i = 0; i < sections.length; i++) {
          const section = sections[i];

          // Render each section to canvas
          const canvas = await html2canvas(section, metLiveStijlen({
            scale: 1.5,
            useCORS: true,
            allowTaint: false,
            imageTimeout: 15000,
            backgroundColor: '#ffffff',
            logging: false,
          }));

          const imgWidth = pageWidth - (2 * margin);
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          // Check if section fits on current page (skip check for first two sections: header + description)
          if (i >= 2 && currentY + imgHeight > pageHeight - margin) {
            // Start new page if section doesn't fit
            pdf.addPage();
            currentY = margin;
          }

          // Add section to PDF
          const imgData = canvas.toDataURL('image/jpeg', 0.85);
          pdf.addImage(imgData, 'JPEG', margin, currentY, imgWidth, imgHeight);

          currentY += imgHeight + 5; // Add small gap between sections

          // If we've filled the page, start a new one
          if (currentY > pageHeight - margin - 20) {
            pdf.addPage();
            currentY = margin;
          }
        }

        const filename = `${breadcrumbTranslations[location.pathname.split('/')[1]]} - ${content.title.replace(/[^a-z0-9]/gi, '_')}.pdf`;
        pdf.save(filename);
      }

      trackEvent({
        type: 'pdf_download',
        path: window.location.pathname + window.location.search,
        title: document.title,
        payload: {
          content_type: location.pathname.split('/')[1],
          content_title: content.title,
        }
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('De pdf maken lukte niet. Probeer het opnieuw.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (loading) return <p>Pagina wordt geladen...</p>;
  if (error || !content) return <p>{error || 'Deze pagina bestaat niet of is verplaatst.'}</p>;

  const pathSegment = location.pathname.split('/')[1];
  const breadcrumbText = breadcrumbTranslations[pathSegment] || pathSegment;
  const hasSteps = content.steps && content.steps.length > 0;
  // --- WIJZIGING: De container class is aangepast om altijd de detailpagina stijl te gebruiken ---
  const containerClass = `detail-page-container ${!hasSteps ? 'article-view' : ''}`;

  return (
    <div className={containerClass}>
      <button className="back-button" onClick={() => navigate(-1)}>
        <ChevronUpIcon className="back-icon" />
        <span>Terug</span>
      </button>
      {/* --- WIJZIGING: De header is nu identiek aan DoorloopjeDetail --- */}
      <header className="detail-page-header">
        <div className="header-title-block">
          <p className="detail-breadcrumb">{breadcrumbText}</p>
          <h1 className="detail-title">{content.title}</h1>
        </div>
        <div className="detail-actions-top">
          <button
            className="btn-icon btn-download"
            onClick={handleDownloadPDF}
            disabled={isGeneratingPDF}
          >
            <DownloadIcon className="btn-download-icon" />
            {isGeneratingPDF ? 'GENEREREN...' : 'DOWNLOAD PDF'}
          </button>
        </div>
      </header>

      {/* PDF Export Container (hidden, only used for PDF generation) */}
      <div className={`pdf-export-container pdf-whyhow ${isGeneratingPDF ? 'generating' : ''}`} ref={pdfContentRef}>
        <div className="pdf-header">
          <div className="pdf-title-block">
            <p className="pdf-theme">{breadcrumbText.toUpperCase()}</p>
            <h1 className="pdf-title">{content.title}</h1>
          </div>
          {globalSettings?.logo?.url && (
            <img
              src={proxify(globalSettings.logo.url)}
              alt="Doorloopjes"
              className="pdf-logo"
              crossOrigin="anonymous"
            />
          )}
        </div>

        {content.description && (
          <div className="pdf-description">
            <Beschrijving blokken={content.description} markdown={!hasSteps} />
          </div>
        )}

        {hasSteps && (
          <div className="pdf-steps">
            {content.steps.map((step, index) => {
              const imageUrl = step.image?.url || null;
              return (
                <div key={step.id || index} className="pdf-step">
                  {imageUrl && (
                    <div className="pdf-step-image-container">
                      <img
                        src={proxify(imageUrl)}
                        alt={`Stap ${index + 1}`}
                        className="pdf-step-image"
                        crossOrigin="anonymous"
                      />
                    </div>
                  )}
                  <div className="pdf-step-content">
                    <div className="pdf-step-header">
                      <span className="pdf-step-number">{index + 1}</span>
                      {step.title && <h3 className="pdf-step-title">{step.title}</h3>}
                    </div>
                    {step.description && (
                      <div className="pdf-step-description">
                        <BlocksRenderer content={step.description} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="pdf-footer">
          <p>Gegenereerd via doorloopjes.nl</p>
        </div>
      </div>

      <main className="detail-main-content">
        {content.description && (
          <div className="detail-description">
            <Beschrijving blokken={content.description} markdown={!hasSteps} />
          </div>
        )}
        
        {/* --- WIJZIGING: De weergave van de stappen is nu identiek aan DoorloopjeDetail --- */}
        {hasSteps && (
          <div className="steps-grid">
            {content.steps.map((step, index) => {
              const stepImageUrl = step.image?.url || null;
              return (
                <div key={step.id || index} className="step-card">
                  <div className="step-card-image-container">
                    {stepImageUrl ? (
                      <>
                        <div className="step-card-image-background" style={{ backgroundImage: `url(${stepImageUrl})` }}></div>
                        <img src={stepImageUrl} alt={`Stap ${index + 1}`} className="step-card-image-foreground" />
                      </>
                    ) : (
                      <div className="step-card-image-placeholder"></div>
                    )}
                  </div>
                  <div className="step-card-content">
                    <span className="step-number">{index + 1}</span>
                    <div className="step-description">
                      <h4>{step.title}</h4>
                      {step.description && <BlocksRenderer content={step.description} />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

export default WhyHowDetailPage;